import type { ColumnDef, SortingState } from '@tanstack/react-table';
import axios from 'axios';
import cx from 'clsx';
import { dateToYMDHMS } from 'common/date.ts';
import { fromQuerystring, POST, parseFormValues, toQuerystring, truncate, valueToYESNO } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { Permission } from 'types/Permission.ts';
import type { User } from 'types/User.ts';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { IDelete, ISearch } from 'ui/component/Icons.tsx';
import { Input } from 'ui/control/Input.tsx';
import { AddPermissionForm } from './AddPermissionForm.tsx';
import { DeleteDialog } from './DeleteDialog.tsx';
import { getSWRKeyForPage, Table } from './table/index.ts';

export const getColumns = (t: (key: string) => string, showDerivedColumn: boolean, onDelete: (permission: Permission) => void) => {
  const defaultColumns: ColumnDef<Permission>[] = [
    {
      accessorKey: 'id',
      header: t('components.permissionList.columns.id'),
      size: 120,
      cell: (info) => {
        const id = info.getValue<string>();
        return (
          <CopyableText className="font-mono" copyText={id}>
            {truncate(id, 8, '')}
          </CopyableText>
        );
      },
    },
    {
      header: t('components.permissionList.columns.date'),
      accessorKey: 'created_at',
      minSize: 180,
      cell: (info) => {
        const value = info.getValue<string>();
        return dateToYMDHMS(value);
      },
    },
    {
      accessorKey: 'subject_name',
      header: t('components.permissionList.columns.subject'),
      size: 220,
      meta: { truncate: true },
      cell: (info) => {
        const value = info.getValue<string>();
        const row = info.row.original;
        const subject_id = row.subject;
        if (subject_id === '*') return <span>*</span>;
        else if (value) {
          return row.subject_is_group ? (
            <span>{value}/*</span>
          ) : (
            <Link className="anchor anchor-novisit" to={`/user/${subject_id}`}>
              {value}
            </Link>
          );
        } else {
          return (
            <CopyableText className="font-mono" copyText={row.subject}>
              {truncate(row.subject, 8, '')}
            </CopyableText>
          );
        }
      },
    },
    {
      accessorKey: 'operation',
      header: t('components.permissionList.columns.operation'),
      meta: { truncate: true },
    },
    {
      accessorKey: 'resource',
      header: t('components.permissionList.columns.resource'),
      size: 220,
      meta: { truncate: true },
      cell: (info) => {
        const value = info.getValue<string>();
        const row = info.row.original;
        const resource_id = row.resource;
        if (resource_id === '*') return <span>*</span>;
        else if (value) {
          return row.resource_is_owner ? (
            <span>{value}/*</span>
          ) : (
            // @TODO: change to the correct link. not always user. can be a leg or a shipment, etc
            <Link className="anchor anchor-novisit" to={`/user/${resource_id}`}>
              {value}
            </Link>
          );
        } else {
          return (
            <CopyableText className="font-mono" copyText={row.resource}>
              {truncate(row.resource, 8, '')}
            </CopyableText>
          );
        }
      },
    },
    {
      id: 'derived',
      accessorKey: 'derived',
      size: 150,
      header: t('components.permissionList.columns.derivedFromGroup'),
      cell: (info) => {
        const value = info.getValue<string>();
        const result = valueToYESNO(value);
        return result;
      },
    },
    {
      header: t('components.permissionList.columns.actions'),
      size: 70,
      meta: { hideFallback: true },
      cell: (info) => (
        <div className="flex w-full items-center justify-center">
          {!info.row.original.derived && (
            <IDelete
              className="text-gray-500"
              size={20}
              onClick={() => {
                if (onDelete) onDelete(info.row.original);
              }}
            />
          )}
        </div>
      ),
    },
  ];

  let columns = [...defaultColumns];
  // if user is supplier we need to show other columns
  if (showDerivedColumn === false) {
    columns = columns.filter((column) => column.id !== 'derived');
  }
  return columns;
};

type Props = {
  className?: string;
  user?: User;
  title?: string;
  enableAddPermission: boolean;
  showDerived?: boolean;
};

type FormValues = {
  search: string;
};

export const PermissionList = ({ className, title, user, enableAddPermission, showDerived }: Props) => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [pendingDeletePermission, setPendingDeletePermission] = React.useState<Permission | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sorters, setSorters] = React.useState<SortingState>(
    (showDerived ? [{ id: 'derived', desc: false }] : []).concat(
      ...[
        { id: 'subject', desc: false },
        { id: 'operation', desc: false },
        { id: 'resource', desc: false },
      ],
    ),
  );

  const defaultValues: FormValues = { search: '' };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState } = formContext;

  // if we are in user page, we need to show only permissions for this user, otherwise we need to show all permissions visible to the user
  const subject$in = user ? [user.id, user.owner_id].filter(Boolean) : undefined;
  const swrCount = useSWR(formState.isValid ? ['/permission/count', { ...values, subject$in, operation$not: 'DocumentGet', derived: showDerived }, parseFormValues] : null, POST);
  const swr = useSWRInfinite(
    getSWRKeyForPage('/permission/list', 25, { ...values, subject$in, operation$not: 'DocumentGet', derived: showDerived }, sorters, parseFormValues),
    POST,
  );

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const handleDeletePermissionConfirm = async () => {
    if (!pendingDeletePermission) return;
    await axios.delete(`/permission/${pendingDeletePermission.id}`);
    setPendingDeletePermission(null);
    swr.mutate();
  };

  const columns = React.useMemo(
    () =>
      getColumns(t, showDerived === undefined, (permission) => {
        setPendingDeletePermission(permission);
      }),
    [showDerived, t],
  );

  const onPermissionAdd = () => {
    void swr.mutate();
    void swrCount.mutate();
  };

  return (
    <div className={cx(className, 'flex flex-col gap-4 bg-white')}>
      <DeleteDialog
        isOpen={!!pendingDeletePermission}
        onClose={() => setPendingDeletePermission(null)}
        onConfirm={handleDeletePermissionConfirm}
        title={t('components.permissionList.confirmMessages.deletePermissionTitle')}
        message={t('components.permissionList.confirmMessages.deletePermission')}
      />
      {title && <h4>{title}</h4>}
      {enableAddPermission && (
        <section className="py-0">
          <AddPermissionForm user={user} onAdd={onPermissionAdd} />
        </section>
      )}
      <section>
        <FormProvider {...formContext}>
          <form id="search-form" ref={formRef} className="mb-4 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="flex place-content-between place-items-center">
              <Input
                className="w-[400px]"
                name={'search'}
                placeholder={t('components.permissionList.search.placeholder')}
                required={true}
                autoFocus={true}
                RightIcon={ISearch}
                autoComplete={'off'}
                debouncedAutoSubmitForm={'#search-form'}
                debouncedAutoSubmitTimeout={500}
              />
            </div>
          </form>
        </FormProvider>
        <Table
          className={cx('max-h-[calc(100vh-385px)]')}
          columns={columns}
          swr={swr}
          pagination={true}
          rowsCount={swrCount.data}
          defaultSorting={sorters}
          onColumnSort={setSorters}
          estimateSize={50}
          overscan={50}
        />
      </section>
    </div>
  );
};
